import { captureException } from '@sentry/react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  getInventoryArticleType,
  nextQuantity,
  useParameters,
} from '@web/utils/src';
import cartIcon from '@web/utils/src/assets/icons/functions/cart-icon.svg';
import useCheckItemCartQuantity from '@/hooks/useCheckItemCartQuantity';
import { useCustomToast } from '@/hooks/useCustomToast';
import { cartAtom, isCartOpenAtom, newCartItemAtom } from '@/store/atoms';
import { addToCartAtom } from '@/store/store';
import { Article } from '@/types';
import { AddToCartToast } from '../AddToCartToast';

interface CartButtonProps {
  disableCount?: boolean;
  article?: Article;
}

interface CartParams {
  retailerId: string;
}

const CartButton = (props: CartButtonProps) => {
  const { disableCount = false, article } = props;
  const cart = useAtomValue(cartAtom);
  const setCartOpen = useSetAtom(isCartOpenAtom);
  const setCartItems = useSetAtom(addToCartAtom);
  const newCartItem = useSetAtom(newCartItemAtom);
  const { retailerId } = useParameters<CartParams>();
  const carItems = cart[retailerId] || [];
  const customToast = useCustomToast();
  const { t } = useTranslation();

  const quantityInCart = useCheckItemCartQuantity(article as Article);

  const handleAddToCart = () => {
    let quantity = 1;
    if (!article) return;
    const itemType = getInventoryArticleType(article);

    if (itemType === 'loose' && article.amount) {
      const deliveryOptions = article.quantityOptions?.delivery;
      if (!deliveryOptions || !deliveryOptions.length) {
        captureException(
          new Error(`quantity options not defined on article #${article.id}`),
        );
      }
      const incrementBy = nextQuantity(
        deliveryOptions as string[],
        quantityInCart,
      );
      quantity = incrementBy as number;
    }

    if (article) {
      try {
        newCartItem(article);
        setCartItems({ retailerID: retailerId, quantity });
        toast(
          <AddToCartToast
            article={article}
            onClick={() => setCartOpen(true)}
          />,
          { duration: 3000, position: 'bottom-right' },
        );
      } catch (error: any) {
        customToast('error', error.message || t('global.something_went_wrong'));
        console.log(error);
      }
    }
  };

  const handleCartOpen = () => {
    setCartOpen((prev) => !prev);
  };

  const handleClick = () => {
    return disableCount ? handleAddToCart() : handleCartOpen();
  };

  return (
    <div className="relative cursor-pointer" onClick={handleClick}>
      <button className="bg-primary-green p-1-0 rounded-base flex items-center">
        <img src={cartIcon} className="h-4-0 md:h-1-8 w-auto" />
        <div
          className={clsx(
            carItems.length > 0 && !disableCount
              ? 'w-3-0 md:w-1-6 h-3-0 md:h-1-6'
              : 'w-0 h-0 overflow-hidden',
            'bg-white rounded-full  flex items-center justify-center ml-1-0 transition-all duration-200 ease-linear',
          )}
        >
          <span className="text-2-0 md:text-1-2 leading-0 font-bd text-primary-green">
            {carItems.length}
          </span>
        </div>
      </button>
    </div>
  );
};

export default CartButton;
